import React, { useCallback, useEffect, useRef, useState } from "react";
import EventCard from "./EventCard";
import { useAuthBootstrap } from "../../context/AuthBootstrapContext";
import "./index.css";

const API_BASE = "https://api-2026.fikacreatives.co";

const TYPEFORM_ORIGINS = new Set([
  "https://form.typeform.com",
  "https://embed.typeform.com",
]);

function isTypeformSubmitMessage(data) {
  if (!data) return false;
  if (data.type === "form-submit") return true;
  if (data?.event?.type === "form-submit") return true;
  if (data?.typeform?.type === "form-submit") return true;
  return false;
}

function getOrCreateId(storageKey) {
  try {
    const existing = window.sessionStorage.getItem(storageKey);
    if (existing) return existing;
    const id = crypto.randomUUID();
    window.sessionStorage.setItem(storageKey, id);
    return id;
  } catch {
    return `${storageKey}_${Math.random().toString(16).slice(2)}_${Date.now()}`;
  }
}

async function readJsonSafely(r) {
  const ct = r.headers.get("content-type") || "";
  if (ct.includes("application/json")) return await r.json();
  const text = await r.text();
  return {
    ok: false,
    error: `Expected JSON but got ${ct || "unknown"}: ${text.slice(0, 160)}`,
  };
}

function normalizeEventsArray(rawEvents) {
  const arr = Array.isArray(rawEvents) ? rawEvents : [];
  return arr.map((e) => ({
    ...e,
    id: e.id || e.event_id || e.slug,
    title:
      e.title || e.name || e.event_name || e.event_title || e.eventName || "Untitled event",
  }));
}

const MONTH_NAMES = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];

function getEventMonth(event) {
  const raw = event.event_date_iso || event.event_date_time || event.date || event.start_date;
  if (raw) {
    const d = new Date(raw);
    if (!isNaN(d)) return MONTH_NAMES[d.getMonth()];
  }
  return null;
}

function groupEventsByMonth(events) {
  const groups = [];
  let currentMonth = undefined;
  let currentGroup = null;
  for (const event of events) {
    const month = getEventMonth(event);
    if (month !== currentMonth) {
      currentMonth = month;
      currentGroup = { month, events: [] };
      groups.push(currentGroup);
    }
    currentGroup.events.push(event);
  }
  return groups;
}

export default function Events() {
  const { person } = useAuthBootstrap();
  const [happeningsTab, setHappeningsTab] = useState("upcoming");

  const [events, setEvents] = useState([]);
  const [pastEvents, setPastEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [isPostOpen, setIsPostOpen] = useState(false);
  const iframeRef = useRef(null);

  const [eventSubmitId, setEventSubmitId] = useState(null);
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    const id = getOrCreateId("event_submit_id");
    setEventSubmitId(id);
  }, []);

  const loadEvents = useCallback(async () => {
    setLoading(true);
    setErr("");

    try {
      const personId = person?.id || null;

      const makeUrl = (past) => {
        const url = new URL(`${API_BASE}/api/events`);
        if (past) url.searchParams.set("past", "true");
        if (personId) url.searchParams.set("person_id", personId);
        return url.toString();
      };

      const [upcomingRes, pastRes] = await Promise.all([
        fetch(makeUrl(false), { credentials: "include" }),
        fetch(makeUrl(true), { credentials: "include" }),
      ]);

      const [upcomingData, pastData] = await Promise.all([
        readJsonSafely(upcomingRes),
        readJsonSafely(pastRes),
      ]);

      if (!upcomingRes.ok || !upcomingData?.ok) {
        setErr(upcomingData?.error || "Failed to load events.");
        setEvents([]);
      } else {
        setEvents(normalizeEventsArray(upcomingData.events || []));
      }

      if (pastRes.ok && pastData?.ok) {
        setPastEvents(normalizeEventsArray(pastData.events || []));
      }
    } catch {
      setErr("Network error loading events.");
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [person?.id]);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!alive) return;
      await loadEvents();
    })();
    return () => { alive = false; };
  }, [loadEvents]);

  useEffect(() => {
    if (!isPostOpen) return;
    if (!eventSubmitId) return;

    let handled = false;

    const onMessage = async (event) => {
      if (!event?.data) return;
      if (event.origin && !TYPEFORM_ORIGINS.has(event.origin)) return;

      const iframeWin = iframeRef.current?.contentWindow;
      if (!iframeWin || event.source !== iframeWin) return;

      if (!isTypeformSubmitMessage(event.data)) return;
      if (handled) return;

      handled = true;

      try {
        setSubmitError("");
        setIsPostOpen(false);

        await new Promise((res) => setTimeout(res, 1200));
        await loadEvents();

        try {
          const next = crypto.randomUUID();
          window.sessionStorage.setItem("event_submit_id", next);
          setEventSubmitId(next);
        } catch {
          const next = `event_submit_id_${Math.random().toString(16).slice(2)}_${Date.now()}`;
          window.sessionStorage.setItem("event_submit_id", next);
          setEventSubmitId(next);
        }
      } catch (e) {
        handled = false;
        setSubmitError(e?.message || "Something went wrong submitting your event.");
      }
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [isPostOpen, eventSubmitId, loadEvents]);

  const shownHappenings = happeningsTab === "upcoming" ? events : pastEvents;

  return (
    <div className="eventsPage">
      <main className="eventsMain">
        {err ? <div className="eventsError">{err}</div> : null}
        {submitError ? <div className="eventsError">{submitError}</div> : null}

        <div className="happeningsSubTabs">
          <button
            className={`happeningsTab${happeningsTab === "upcoming" ? " happeningsTab--active" : ""}`}
            onClick={() => setHappeningsTab("upcoming")}
            type="button"
          >
            Upcoming
          </button>
          {pastEvents.length > 0 && (
            <button
              className={`happeningsTab${happeningsTab === "past" ? " happeningsTab--active" : ""}`}
              onClick={() => setHappeningsTab("past")}
              type="button"
            >
              Past
            </button>
          )}
        </div>

        {loading ? (
          <div className="eventsLoading">Loading…</div>
        ) : shownHappenings.length === 0 ? (
          <div className="eventsLoading">
            {happeningsTab === "upcoming"
              ? "Check back in for upcoming Fika events!"
              : "No past events yet."}
          </div>
        ) : (
          groupEventsByMonth(shownHappenings).map(({ month, events: monthEvents }) => (
            <div key={month || "unknown"} className="eventsMonthSection">
              {month && <div className="eventsMonthHeader">{month}</div>}
              <div className="eventsGrid">
                {monthEvents.map((event) => (
                  <EventCard key={event.id || event.event_id || event.slug} event={event} />
                ))}
              </div>
            </div>
          ))
        )}
      </main>
    </div>
  );
}
